import { getDocumentLanguage } from 'app/locales/languageMapping';
import { cookieCountries, getTextDirection } from 'app/modules/Constants';
import KlaviyoScript from 'app/scripts/KlaviyoScript';
import PixelScript from 'app/scripts/PixelScript';
import { DefaultSeo } from 'next-seo';
import type { AppProps } from 'next/app';
import { Assistant, Montserrat } from 'next/font/google';
import { useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';
import { Provider, connect } from 'react-redux';
import { agreeCookieNotice, setLanguage } from '../actions/baseActions';
import { useStore } from '../store';
import '../styles/app.scss';
import '../styles/responsive.scss';

const montserrat = Montserrat({
	subsets: ['latin-ext', 'cyrillic-ext'],
	display: 'swap',
	weight: ['400', '600'],
});
const assistant = Assistant({
	subsets: ['latin-ext', 'hebrew'],
	display: 'swap',
	weight: ['400', '500', '600', '700', '800'],
	variable: '--font-assistant',
});

const GA_MEASUREMENT_ID = 'G-HRP9HYBP28';

export const initializeConsent = () => {
	if (typeof window !== 'undefined') {
		window.dataLayer = window.dataLayer || [];
		window.gtag =
			window.gtag ||
			function () {
				// eslint-disable-next-line prefer-rest-params
				(window.dataLayer = window.dataLayer || []).push(arguments);
			};

		window.gtag('consent', 'default', {
			functionality_storage: 'denied',
			security_storage: 'granted',
			ad_personalization: 'denied',
			ad_storage: 'denied',
			ad_user_data: 'denied',
			analytics_storage: 'denied',
			personalization_storage: 'denied',
			region: cookieCountries,
		});

		window.gtag('set', 'ads_data_redaction', true);
		window.gtag('set', 'url_passthrough', false);

		// NOTE: the earlier localStorage User-ID seed was REMOVED here. uid/imid rotate per
		// session, so the persisted `tpdrops-uid`/`tpdrops-imid` is a STALE prior-session id.
		// Seeding it made the entry events (page_view/home_page_view) carry the *previous*
		// session's user_id while search_submission carried the *current* one, breaking the
		// funnel join on user_id (BigQuery: returning-user home_page_view↔search_submission
		// match was ~26% vs ~96% for new users; BHP, which never seeds, is ~99.9%). The entry
		// events now attach the CURRENT-session id from Redux `state.base` instead — see the
		// page_view effect below and use-home-page-view-ga-event.

		// SF-29: hand-rolled gtag bootstrap, replacing the `@next/third-parties`
		// <GoogleAnalytics> component. `send_page_view: false` suppresses the
		// synchronous auto page_view that previously fired before `user_id` was set
		// (the root cause: ~0% user_id on entry events). gtag.js itself is loaded
		// early via the <Script> in App so `window.gtag('event', …)` from pages never
		// drops; the identified `page_view` is fired manually once `user_id` is ready.
		window.gtag('js', new Date());
		window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
	}
};

type AppOwnProps = { uid: string | null; imid: string | null; setLanguage: (lng: string) => void };

function App({ Component, pageProps, uid, imid, setLanguage }: AppProps & AppOwnProps) {
	const [renderScript, setRenderScript] = useState(false);
	const [hydrated, setHydrated] = useState(false);
	const [isSiteLoaded, setIsSiteLoaded] = useState(false);
	const pageViewFiredRef = useRef(false);

	const store = useStore(pageProps.initialReduxState);
	const params = useSearchParams();
	const lng = params?.get('lng') as string;

	if (lng && typeof document !== 'undefined') {
		// Base language as in tpdrops (`de-DE` → `de`, `es-LA` → `es`), mirrors the SSR value from _document.
		document.documentElement.lang = getDocumentLanguage(lng);
		// Mirrors the SSR value from _document so client-side navigations flip direction too.
		document.documentElement.dir = getTextDirection(lng);
	}

	useEffect(() => {
		// Keep Redux `state.base.lng` in sync with ?lng= so RTL-aware components
		// (DatePicker dir, autocomplete locale) don't depend on the localStorage cache.
		if (lng) {
			setLanguage(lng);
		}
	}, [lng, setLanguage]);

	useEffect(() => {
		let agreeCookieNoticeValue;
		try {
			agreeCookieNoticeValue = localStorage.getItem('cookieNotice');
			if (agreeCookieNoticeValue) {
				store.dispatch(agreeCookieNotice());
			}
		} catch (error) {
			console.error('Failed to read from localStorage, fallback to default value:', error);
		}
		initializeConsent();
		setHydrated(true);
	}, []);

	useEffect(() => {
		if (process.env.NODE_ENV !== 'development') {
			if ('serviceWorker' in navigator) {
				window.addEventListener('load', () => {
					const version = '1.3.16';
					navigator.serviceWorker
						.register(`/service-worker.js?version=${version}`)
						.then((registration) => {
							console.log('Service worker version:', version);
						})
						.catch((error) => {
							console.error('Service Worker registration failed:', error);
						});
				});
			}
		}
		const onLoad = () => {
			setIsSiteLoaded(true);
			setRenderScript(!window.location.pathname.includes('/passthru'));
		};

		if (document.readyState === 'complete') {
			// Already loaded at mount: run the same setup as the load event, including renderScript,
			// otherwise Cookiebot + Clarity would never inject on already-complete pages.
			onLoad();
		} else {
			window.addEventListener('load', onLoad);
		}

		return () => {
			window.removeEventListener('load', onLoad);
		};
	}, []);

	// SF-29: fire the GA4 `page_view` manually (the auto one is suppressed via
	// `send_page_view: false`) once the CURRENT-session User-ID is available, so entry
	// events carry it. The id comes from Redux `state.base` (uid/imid), populated when
	// `init('flights')` resolves — NOT the persisted localStorage id, which rotates per
	// session and would be a stale prior-session value. A 2s timeout guarantees the
	// page_view still fires (without user_id) if init is slow or fails — never drop it.
	useEffect(() => {
		if (pageViewFiredRef.current) return;
		if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

		const firePageView = () => {
			if (pageViewFiredRef.current) return;
			pageViewFiredRef.current = true;
			// Attach the current-session User-ID (built exactly like baseActions.init's set)
			// so page_view carries the same user_id as search_submission. On the timeout
			// fallback (uid still absent) it fires WITHOUT user_id rather than a stale id.
			const userId = [uid, imid].filter(Boolean).join('') || undefined;
			if (userId) {
				window.gtag('set', { user_id: userId });
			}
			window.gtag('event', 'page_view');
		};

		if (uid) {
			firePageView();
			return;
		}

		const timeoutId = setTimeout(firePageView, 2000);
		return () => clearTimeout(timeoutId);
	}, [uid, imid]);

	if (!hydrated) {
		// Returns null on first render, so the client and server match
		return null;
	}

	return (
		<>
			<DefaultSeo
				openGraph={{
					type: 'website',
					locale: 'en_IE',
					url: process.env.NEXT_PUBLIC_SITE_URL,
					site_name: process.env.NEXT_PUBLIC_SITE_NAME,
				}}
			/>
			<Script
				id="gtag-js"
				strategy="afterInteractive"
				src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
			/>
			<PixelScript pixelId={process.env.NEXT_PUBLIC_FB_PIXEL_ID} />
			<KlaviyoScript company_id={process.env.NEXT_PUBLIC_KLAVIYO_COMPANY_ID} />
			{isSiteLoaded && (
				<>
					{renderScript && (
						<>
							<Script
								strategy="lazyOnload"
								id="Cookiebot"
								src="https://consent.cookiebot.com/uc.js"
								data-cbid="266f03c3-619e-4278-9ea0-072c1b4e36c5"
								type="text/javascript"
							/>
							{process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID && (
								<Script id="microsoft-clarity" strategy="afterInteractive">
									{`
										(function(c,l,a,r,i,t,y){
											c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
											t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i+"?ref=gtm2";
											y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
										})(window, document, "clarity", "script", "${process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID}");
									`}
								</Script>
							)}
						</>
					)}
				</>
			)}

			<main className={`${montserrat.className} ${assistant.variable}`}>
				<Component {...pageProps} />
			</main>
		</>
	);
}

const mapStateToProps = (state: { base: { uid: string | null; imid: string | null } }) => ({
	uid: state.base.uid,
	imid: state.base.imid,
});

const ConnectedMyApp = connect(mapStateToProps, { setLanguage, agreeCookieNotice })(App);

function AppWithProvider({ Component, pageProps, router }: AppProps) {
	const store = useStore(pageProps.initialReduxState);
	// const { growthbook, callback: growthbookCallback } = useGrowthbook();
	// store.dispatch(setGrowthbookCallback(growthbookCallback));

	return (
		// <GrowthBookProvider growthbook={growthbook}>
		<Provider store={store}>
			{/* <GrowthbookFeature featureId="sf-google-signin"> */}
			{/* {!Helper.isMobile() && <GoogleSignin />} */}
			{/* </GrowthbookFeature> */}
			<ConnectedMyApp Component={Component} pageProps={pageProps} router={router} />
		</Provider>
		// </GrowthBookProvider>
	);
}

export default AppWithProvider;
